import Vue from 'vue'
import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/AdvertisersCost/helpers'
import advertisersCostRepository from '@/repository/generatedRepository/advertisersCostRepository'
import TableOptions from '@/models/TableOptions'
import { getSortedItems, omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'

const state = {
  list: [],
  sortedList: [],
  filters: StorageService.get(storageKeys.ADVERTISERS_COST_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  list: state => state.list,
  sortedList: state => state.sortedList,
  isOpenFilterPanel: state =>
    state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadData(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.ADVERTISERS_COST_FILTERS, filters)

    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
      cfFilter: appliedCfFilters,
      pagination: {
        limit: -1,
      },
    })
    delete preparedFilters.order
    let { data } = await advertisersCostRepository.list(preparedFilters)
    await context.dispatch('checkDuplicates', data)
    context.commit('setList', { data, filters })
    return { products: data.items, pagination: data.pagination }
  },
  async searchAdvertisersCost(context, filters) {
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
    })

    const { data } = await advertisersCostRepository.list(preparedFilters)

    return { products: data.items, pagination: data.pagination }
  },
  checkDuplicates(context, data) {
    const dataArray = Array.isArray(data) ? data : data.items
    const groupMap = new Map()
    dataArray.forEach(item => {
      const key = `${item.advertiser?.id ?? ''}_${item.country?.id ?? ''}_${item.status ?? ''}`
      if (!groupMap.has(key)) groupMap.set(key, [])
      groupMap.get(key).push(item.id)
    })
    dataArray.forEach(item => {
      const key = `${item.advertiser?.id ?? ''}_${item.country?.id ?? ''}_${item.status ?? ''}`
      const group = groupMap.get(key)
      item.hasDuplicate = group.length > 1 ? group.filter(id => id !== item.id) : []
    })
  },
  async loadItem(context, id) {
    const { data } = await advertisersCostRepository.get(id)
    return data
  },
  async createItem(context, payload) {
    const { idx, isCopy, preparedData } = payload
    // const res = await advertisersCostRepository.post(preparedData)
    context.commit('ADD_LIST_ENTITY', {
      data: { ...preparedData, _status: 'new', hasDuplicate: [] },
      idx,
      isCopy,
    })
  },
  updateItem(context, payload) {
    const { id, ...data } = payload
    return advertisersCostRepository.put(id, data)
  },
  async deleteItem(context, payload) {
    context.commit('REMOVE_LIST_ENTITY', payload)
    await context.dispatch('checkDuplicates', context.state.sortedList)
  },
  async bulkDelete(context, payload) {
    const params = new URLSearchParams()
    payload.forEach(id => params.append('ids[]', id))
    await advertisersCostRepository.bulkDelete(params.toString())
    context.commit('REMOVE_LIST_ENTITY', payload)
  },
  async bulkUpdate(context, payload) {
    await advertisersCostRepository.bulkUpdate(payload)
  },
  async bulkCreate(context, payload) {
    return await advertisersCostRepository.bulkCreate(payload)
  },
  async saveToGoogleSheet(context, payload) {
    await advertisersCostRepository.saveToSheet(payload)
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.ADVERTISERS_COST_FILTERS)
  },
  onRemoveDeletedItems({ commit }) {
    commit('REMOVE_DELETED_ITEMS')
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.ADVERTISERS_COST_FILTERS, updatedFilters)
  },
  updateSort(context, filters) {
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.ADVERTISERS_COST_FILTERS, filters)
    context.commit('SET_SORT', {
      sortBy: filters.sortBy,
      sortDesc: filters.sortDesc,
    })
  },
}

const mutations = {
  setList(state, { data, filters }) {
    state.list = [...data.items]
    state.sortedList = [...getSortedItems(state.list, {
      sortBy: state.filters.sortBy,
      sortDesc: state.filters.sortDesc,
    }, 'advertiserCost')]
    state.tableOptions = new TableOptions(data.pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  ADD_LIST_ENTITY(state, {data, idx, isCopy}) {
    if(!isCopy) state.sortedList.unshift(data)
    else state.sortedList.splice(idx + 1, 0, data)
  },
  REMOVE_LIST_ENTITY(state, item) {
    if (item._status === 'new') {
      state.sortedList = state.sortedList.filter(el => el.id !== item.id)
    } else {
      Vue.set(item, '_status', 'deleted')
    }
  },
  REMOVE_DELETED_ITEMS(state) {
    state.sortedList = state.sortedList.filter(el => el._status !== 'deleted')
  },
  SET_SORT(state, {sortBy, sortDesc}) {
    state.tableOptions = {
      ...state.tableOptions,
      sortBy,
      sortDesc,
    }
    state.sortedList = [...getSortedItems(state.sortedList, {
      sortBy: state.filters.sortBy,
      sortDesc: state.filters.sortDesc,
    }, 'advertiserCost')]
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}